/**
 * Auth Event Tracking Utility
 *
 * Handles analytics event tracking for authentication events.
 * Sends events to both PostHog and Google Tag Manager (GTM).
 * Includes Facebook cookie data for conversion tracking.
 */

import { User } from '@supabase/supabase-js';
import { getCookie } from '../services/analytics';
import { isDuplicateEvent } from './eventDeduplication';
import { deduplicatedPostHogCall } from './posthogDeduplication';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

function generateEventId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Track an authentication event to PostHog and GTM
 */
export function trackAuthEvent(
  posthog: any,
  eventName: string,
  properties: Record<string, any>,
  user: User | null
): void {
  // Build event data with user information
  const eventData: Record<string, any> = {
    ...properties,
    external_id: user?.id || properties.external_id || '',
    user_id: user?.id || properties.user_id || '',
  };

  // Add email (priority: properties.email > user.email)
  if (properties.email) {
    eventData.email = properties.email;
  } else if (user?.email) {
    eventData.email = user.email;
  }

  const emailValue = eventData.email;

  // Remove empty values (but preserve email)
  Object.keys(eventData).forEach((key) => {
    if (key === 'email' && eventData.email) return;
    if (eventData[key] === undefined || eventData[key] === null || eventData[key] === '') {
      delete eventData[key];
    }
  });

  // Check for duplicate events
  if (isDuplicateEvent(eventName, eventData)) {
    return;
  }

  // Generate unique event ID
  if (!eventData.event_id) {
    eventData.event_id = generateEventId();
  }

  // Get Facebook cookies for conversion tracking
  const fbp = typeof window !== 'undefined' ? getCookie('_fbp') || '' : '';
  const fbc = typeof window !== 'undefined' ? getCookie('_fbc') || '' : '';

  eventData.fbp = fbp;
  if (fbc) {
    eventData.fbc = fbc;
  }

  // Send to Google Tag Manager
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];

    const userId = eventData.user_id || eventData.external_id || '';
    const gtmPayload: Record<string, any> = {
      event: eventName,
      event_id: eventData.event_id,
      external_id: userId,
      fbp: fbp,
      ...eventData,
    };

    if (userId) {
      gtmPayload.user_id = userId;
    }

    // Clean empty values (preserve fbp, event, email)
    Object.keys(gtmPayload).forEach((key) => {
      if (key === 'fbp' || key === 'event' || key === 'email') return;
      if (gtmPayload[key] === undefined || gtmPayload[key] === null || gtmPayload[key] === '') {
        delete gtmPayload[key];
      }
    });

    if (emailValue && !gtmPayload.email) {
      gtmPayload.email = emailValue;
    }

    window.dataLayer.push(gtmPayload);
  }

  // Send to PostHog
  if (posthog && typeof posthog.capture === 'function') {
    deduplicatedPostHogCall(posthog, 'capture', eventName, eventData);
  }
}
